import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { RichTextEditor } from "../components/RichTextEditor";
import { MediaPicker, mediaUrl, type MediaAsset } from "../components/MediaPicker";
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Select,
  Textarea,
} from "../components/ui";
import type { PostRow } from "./Blog";

interface PostDetail extends PostRow {
  bodyHtml: string;
}

const CATEGORIES = ["Writing", "Getting found", "How it works", "Automation", "Security"];

interface Draft {
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  bodyHtml: string;
  coverId: string | null;
}

const BLANK: Draft = {
  title: "",
  slug: "",
  excerpt: "",
  category: CATEGORIES[0]!,
  bodyHtml: "",
  coverId: null,
};

/**
 * Writing one post — new or existing, on the same screen.
 *
 * A new post writes nothing to the database until you save. The old flow asked
 * for a title, created a row, and dropped you back on the list to go and find
 * it; abandoning a post halfway through left an empty draft behind for somebody
 * to clean up later. Here there is one screen, and one decision at the end of
 * it.
 *
 * Save and publish stay separate buttons, because they are still separate
 * decisions: one is cheap and frequent, the other fixes an address and puts a
 * page where anyone can link to it. Publish does save first, though — clicking
 * it having just typed a sentence and getting the version without that sentence
 * is a trap, not a safeguard.
 */
export function PostEditor({ isNew = false }: { isNew?: boolean }) {
  // Empty on the "new" route, which has no id segment to read. Every use of it
  // below is already behind `isNew`.
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [picking, setPicking] = useState(false);
  const [pickTarget, setPickTarget] = useState<"cover" | "body">("cover");
  const [resolveImage, setResolveImage] = useState<
    ((value: { src: string; alt: string } | null) => void) | null
  >(null);

  const [draft, setDraft] = useState<Draft | null>(isNew ? BLANK : null);
  /**
   * Whether the address has been typed into. Until it has, it follows the
   * title — which is what someone naming a post expects — but the moment it is
   * edited by hand it stops moving underneath them.
   */
  const [slugEdited, setSlugEdited] = useState(false);

  const post = useQuery({
    queryKey: ["post", id],
    queryFn: () => api<PostDetail>(`/api/v1/admin/posts/${id}`),
    enabled: !isNew,
  });

  // Seeded once the post arrives. Re-seeding on every render would fight the
  // author for control of the fields they are typing into.
  useEffect(() => {
    if (post.data === undefined || draft !== null) return;
    setDraft({
      title: post.data.title,
      slug: post.data.slug,
      excerpt: post.data.excerpt,
      category: post.data.category,
      bodyHtml: post.data.bodyHtml,
      coverId: post.data.cover?.id ?? null,
    });
    setSlugEdited(true);
  }, [post.data, draft]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["post", id] });
    void queryClient.invalidateQueries({ queryKey: ["posts"] });
  };

  /** Creates on a new post, updates on an existing one. Returns the saved row. */
  const persist = async (body: Draft): Promise<PostDetail> => {
    if (!isNew) {
      return api<PostDetail>(`/api/v1/admin/posts/${id}`, { method: "PATCH", body });
    }
    return api<PostDetail>("/api/v1/admin/posts", { method: "POST", body });
  };

  /**
   * Where every write lands.
   *
   * The row goes into the cache before anything navigates, so the editor
   * arrives at a post it is already holding rather than blinking through a
   * loading state — and so the badge does not show "Draft" for a moment on a
   * post that was just published.
   */
  const landOn = (saved: PostDetail) => {
    queryClient.setQueryData(["post", saved.id], saved);
    void queryClient.invalidateQueries({ queryKey: ["posts"] });
    if (isNew) navigate(`/blog/${saved.id}`, { replace: true });
    else void queryClient.invalidateQueries({ queryKey: ["post", saved.id] });
  };

  const save = useMutation({
    mutationFn: () => persist(draft ?? BLANK),
    onSuccess: landOn,
  });

  const publish = useMutation({
    mutationFn: async () => {
      const saved = await persist(draft ?? BLANK);
      return api<PostDetail>(`/api/v1/admin/posts/${saved.id}/status`, {
        method: "POST",
        body: { status: "published" },
      });
    },
    onSuccess: landOn,
  });

  const unpublish = useMutation({
    mutationFn: () =>
      api<PostDetail>(`/api/v1/admin/posts/${id}/status`, {
        method: "POST",
        body: { status: "draft" },
      }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: () => api(`/api/v1/admin/posts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["posts"] });
      navigate("/blog");
    },
  });

  // The error check comes first: a post that failed to load never seeds a
  // draft, so checking for the draft first would leave a missing post spinning
  // on "Loading…" for ever instead of saying what happened.
  if (post.isError) {
    const missing = post.error instanceof ApiError && post.error.status === 404;
    return (
      <div className="space-y-4">
        <ErrorNote>{missing ? "No such post." : "Could not load this post."}</ErrorNote>
        <Link to="/blog" className="text-sm text-accent underline underline-offset-4">
          Back to the blog
        </Link>
      </div>
    );
  }
  if (draft === null) return <p className="text-sm text-dim">Loading…</p>;

  const live = post.data?.status === "published";
  const busy = save.isPending || publish.isPending || unpublish.isPending;

  const setTitle = (title: string) =>
    setDraft({ ...draft, title, ...(slugEdited ? {} : { slug: slugify(title) }) });

  const openPicker = (target: "cover" | "body") =>
    new Promise<{ src: string; alt: string } | null>((resolve) => {
      setPickTarget(target);
      setPicking(true);
      // Stored as a thunk: React would otherwise call the function it is given
      // as an updater and store its return value instead of the function.
      setResolveImage(() => resolve);
    });

  const handlePick = (asset: MediaAsset) => {
    if (pickTarget === "cover") {
      setDraft({ ...draft, coverId: asset.id });
    } else {
      resolveImage?.({ src: mediaUrl(asset.id), alt: asset.alt ?? "" });
    }
    setPicking(false);
    setResolveImage(null);
  };

  const closePicker = () => {
    resolveImage?.(null);
    setPicking(false);
    setResolveImage(null);
  };

  return (
    <div className="space-y-6">
      <nav>
        <Link to="/blog" className="text-sm text-dim underline-offset-4 hover:underline">
          ← Blog
        </Link>
      </nav>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-xl text-ink">
            {draft.title === "" ? (isNew ? "New post" : "Untitled") : draft.title}
          </h1>
          <p className="text-sm text-dim tabular">
            /{draft.slug === "" ? "…" : draft.slug}
            {post.data !== undefined && ` · ${post.data.readingMinutes} min read`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={live ? "live" : "quiet"}>
            {live ? "Published" : isNew ? "Not saved" : "Draft"}
          </Badge>
          <Button variant="primary" disabled={busy} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : isNew ? "Save draft" : "Save"}
          </Button>
          {live ? (
            <Button disabled={busy} onClick={() => unpublish.mutate()}>
              {unpublish.isPending ? "Unpublishing…" : "Unpublish"}
            </Button>
          ) : (
            <Button disabled={busy} onClick={() => publish.mutate()}>
              {publish.isPending ? "Publishing…" : "Publish"}
            </Button>
          )}
        </div>
      </header>

      {save.isError && <ErrorNote>{messageFor(save.error)}</ErrorNote>}
      {publish.isError && <ErrorNote>{messageFor(publish.error)}</ErrorNote>}
      {unpublish.isError && <ErrorNote>{messageFor(unpublish.error)}</ErrorNote>}
      {remove.isError && <ErrorNote>{messageFor(remove.error)}</ErrorNote>}

      <Card title="The post">
        <div className="grid gap-4 p-4 md:grid-cols-2">
          <Field label="Title">
            <Input
              value={draft.title}
              autoFocus={isNew}
              placeholder="What is the post about?"
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>

          <Field
            label="Address"
            hint={
              live
                ? "Fixed once published — every existing link points here."
                : slugEdited
                  ? "Part of the URL. Changeable until this goes out."
                  : "Follows the title until you change it yourself."
            }
          >
            <Input
              value={draft.slug}
              readOnly={live}
              className={live ? "tabular text-dim" : "tabular"}
              onChange={(event) => {
                setSlugEdited(true);
                setDraft({ ...draft, slug: event.target.value });
              }}
            />
          </Field>

          <Field label="Category">
            <Select
              value={draft.category}
              onChange={(event) => setDraft({ ...draft, category: event.target.value })}
            >
              {[...new Set([draft.category, ...CATEGORIES])].filter(Boolean).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Cover image" hint="Shown on the card and at the top of the post.">
            <div className="flex items-center gap-3">
              {draft.coverId !== null && (
                <img
                  src={mediaUrl(draft.coverId)}
                  alt=""
                  className="h-12 w-16 rounded border border-hairline object-cover"
                />
              )}
              <Button type="button" onClick={() => void openPicker("cover")}>
                {draft.coverId === null ? "Choose" : "Change"}
              </Button>
              {draft.coverId !== null && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setDraft({ ...draft, coverId: null })}
                >
                  Remove
                </Button>
              )}
            </div>
          </Field>

          <div className="md:col-span-2">
            <Field
              label="Excerpt"
              hint="One line. It is the card summary and the search description, so it is doing two jobs."
            >
              <Textarea
                rows={2}
                value={draft.excerpt}
                onChange={(event) => setDraft({ ...draft, excerpt: event.target.value })}
              />
            </Field>
          </div>
        </div>
      </Card>

      {picking && <MediaPicker onPick={handlePick} onClose={closePicker} />}

      <Card title="Body">
        <div className="p-4">
          <RichTextEditor
            value={draft.bodyHtml}
            onChange={(bodyHtml) => setDraft({ ...draft, bodyHtml })}
            onRequestImage={() => openPicker("body")}
          />
          <p className="mt-3 text-xs text-dim">
            Formatting outside the allowlist is removed when this is saved — the toolbar only
            offers what survives.
          </p>
        </div>
      </Card>

      {!isNew && !live && (
        <Card title="Delete">
          <div className="space-y-3 p-4">
            <p className="text-sm text-muted">
              A draft can be deleted outright. A published post cannot — unpublish it first, so
              nobody holding a link meets a 404 without warning.
            </p>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Delete "${draft.title || "Untitled"}"? This cannot be undone.`)) {
                  remove.mutate();
                }
              }}
            >
              Delete this draft
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * The address a title would produce.
 *
 * Mirrors `normaliseSlug` in the API, which runs on every write regardless —
 * this exists so the field shows what will happen rather than leaving the
 * author to find out after saving.
 */
function slugify(input: string): string {
  return input
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function messageFor(error: unknown): string {
  return error instanceof ApiError ? error.message : "Something went wrong.";
}
